class DDCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('[data-dd-carousel-track]');
    this.items = Array.from(this.querySelectorAll('[data-dd-carousel-item]'));
    this.controls = this.querySelector('[data-dd-carousel-controls]');
    this.previousButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.progress = this.querySelector('[data-dd-carousel-progress]');
    this.indicator = this.querySelector('[data-dd-carousel-indicator]');
    this.snapPositions = [];
    this.managedScrollPadding = { start: null, end: null };
    this.pauseReasons = new Set();
    this.visible = true;
    this.timer = null;
    this.focusoutTimer = null;
    this.pointerId = null;
    this.pointerTarget = null;
    this.dragTarget = null;
    this.pointerReleaseController = null;
    this.lastPosition = null;
    this.reconnectPosition = null;
    this.pendingAlignment = null;
  }

  connectedCallback() {
    if (!this.track) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
    this.track.addEventListener('pointerdown', (event) => {
      this.finishAlignment(true);
      this.clearPointer();
      this.pointerId = event.pointerId;
      this.pointerTarget = event.target;
      this.pause('pointer');
      this.pointerReleaseController = new AbortController();
      this.ownerDocument.addEventListener('pointerup', releasePointer, {
        capture: true,
        passive: true,
        signal: this.pointerReleaseController.signal,
      });
    }, { signal });
    const releasePointer = (event) => {
      if (event.pointerId !== this.pointerId) return;
      this.clearPointer();
      this.resumeFrom('pointer');
    };
    this.track.addEventListener('pointerup', releasePointer, { signal });
    this.track.addEventListener('pointercancel', releasePointer, { signal });
    this.track.addEventListener('dragstart', (event) => {
      if (this.pointerId !== null && this.pauseReasons.has('pointer') && event.target.contains(this.pointerTarget)) {
        this.dragTarget = event.target;
      }
    }, { signal });
    this.track.addEventListener('dragend', (event) => {
      if (event.target !== this.dragTarget) return;
      this.clearPointer();
      this.resumeFrom('pointer');
    }, { signal });
    this.track.addEventListener('wheel', () => { this.finishAlignment(true); this.restartTimer(); }, { passive: true, signal });
    this.addEventListener('pointerover', (event) => {
      const control = event.pointerType === 'mouse' ? event.target.closest?.('[data-dd-carousel-hover-pause]') : null;
      if (control && this.contains(control) && !control.contains(event.relatedTarget)) this.pause('media-hover');
      else this.syncArtworkHover(event);
    }, { signal });
    this.addEventListener('pointermove', (event) => this.syncArtworkHover(event), { passive: true, signal });
    this.addEventListener('pointerout', (event) => {
      const control = event.pointerType === 'mouse' ? event.target.closest?.('[data-dd-carousel-hover-pause]') : null;
      if (control && this.contains(control) && !control.contains(event.relatedTarget)) this.resumeFrom('media-hover');
      const artwork = event.pointerType === 'mouse' ? event.target.closest?.('.dd-brand-artwork') : null;
      if (artwork && !artwork.contains(event.relatedTarget)) this.resumeFrom('media-hover');
    }, { signal });
    this.addEventListener('pointerleave', (event) => { if (event.pointerType === 'mouse') this.resumeFrom('media-hover'); }, { signal });

    this.addEventListener('focusin', (event) => {
      this.finishAlignment(true);
      const pointerControl = this.pointerTarget?.closest?.('a, button, input, select, textarea, [role="button"]');
      if (pointerControl?.contains(event.target)) this.resumeFrom('focus');
      else {
        this.pause('focus');
        this.revealFocusedItem(event.target);
      }
    }, { signal });
    this.addEventListener(
      'focusout',
      () => {
        this.clearFocusoutTimer();
        this.focusoutTimer = window.setTimeout(() => {
          this.focusoutTimer = null;
          if (!this.isConnected) return;
          if (!this.contains(document.activeElement)) this.resumeFrom('focus');
        }, 0);
      },
      { signal }
    );

    this.previousButton?.addEventListener('click', (event) => this.onControlClick(event, -1), { signal });
    this.nextButton?.addEventListener('click', (event) => this.onControlClick(event, 1), { signal });
    document.addEventListener('visibilitychange', () => this.onVisibilityChange(), { signal });

    this.resizeObserver = new ResizeObserver(() => this.refresh());
    this.resizeObserver.observe(this.track);

    this.mutationObserver = new MutationObserver(() => this.refresh());
    this.mutationObserver.observe(this.track, { childList: true });

    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.visible = entry?.isIntersecting ?? true;
        if (this.visible) this.schedule();
        else this.clearTimer();
      },
      { threshold: 0.05 }
    );
    this.intersectionObserver.observe(this);

    this.refresh();
  }

  disconnectedCallback() {
    this.finishAlignment(true);
    this.reconnectPosition = this.lastPosition;
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.clearFocusoutTimer();
    this.clearTimer();
    this.pauseReasons.clear();
    this.clearPointer();
  }

  clearPointer() {
    this.pointerReleaseController?.abort();
    this.pointerReleaseController = null;
    this.pointerId = null;
    this.pointerTarget = null;
    this.dragTarget = null;
  }

  revealFocusedItem(target) {
    const item = target.closest?.('[data-dd-carousel-item]');
    if (!item || !this.track.contains(item) || !target.matches?.(':focus-visible')) return;

    const trackRect = this.track.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const left = trackRect.left + this.track.clientLeft;
    const right = left + this.track.clientWidth;
    if (itemRect.left >= left - 0.5 && itemRect.right <= right + 0.5) return;

    const start = this.isRTL ? right - itemRect.right : itemRect.left - left;
    const end = this.isRTL ? right - itemRect.left : itemRect.right - left;
    const current = this.logicalScroll;
    // Prefer an existing snap that reveals the whole card without moving the page.
    const destination = this.snapPositions
      .filter((position) => position >= current + end - this.track.clientWidth - 0.5 && position <= current + start + 0.5)
      .sort((a, b) => Math.abs(a - current) - Math.abs(b - current))[0];
    this.scrollToPosition(destination ?? Math.max(this.firstSnap, Math.min(this.lastSnap, current + start)), 'instant');
  }

  get reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get autoscroll() {
    return this.dataset.autoscroll === 'true' && !this.reducedMotion;
  }

  get loops() {
    return this.dataset.endBehavior === 'loop';
  }

  get dwell() {
    const seconds = Number.parseInt(this.dataset.autoscrollSpeed || '5', 10);
    return Math.max(3, Math.min(10, Number.isFinite(seconds) ? seconds : 5)) * 1000;
  }

  get maxScroll() {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  }

  syncArtworkHover(event) {
    if (event.pointerType !== 'mouse' || event.target.closest?.('[data-dd-carousel-hover-pause]')) return;
    const artwork = event.target.closest?.('.dd-brand-artwork');
    const item = artwork?.closest?.('[data-dd-carousel-item]');
    if (!artwork || !item || !this.track.contains(item)) return this.resumeFrom('media-hover');
    const box = artwork.getBoundingClientRect();
    const halfWidth = Math.min(48, box.width / 3) / 2;
    const halfHeight = Math.min(48, box.height / 3) / 2;
    const insideCentre = Math.abs(event.clientX - (box.left + box.width / 2)) <= halfWidth
      && Math.abs(event.clientY - (box.top + box.height / 2)) <= halfHeight;
    if (insideCentre) this.pause('media-hover');
    else this.resumeFrom('media-hover');
  }

  get isRTL() {
    return window.getComputedStyle(this.track).direction === 'rtl';
  }

  get logicalScroll() {
    return this.isRTL ? -this.track.scrollLeft : this.track.scrollLeft;
  }

  get firstSnap() {
    return this.snapPositions[0] ?? 0;
  }

  get lastSnap() {
    return this.snapPositions[this.snapPositions.length - 1] ?? this.maxScroll;
  }

  get scrollRange() {
    return Math.max(0, this.lastSnap - this.firstSnap);
  }

  get logicalPosition() {
    return Math.max(this.firstSnap, Math.min(this.lastSnap, this.logicalScroll));
  }

  get overflowing() {
    return this.items.length > 1 && this.snapPositions.length > 1 && this.scrollRange > 1;
  }

  get atStart() {
    return this.logicalPosition <= this.firstSnap + 2;
  }

  get atEnd() {
    return this.logicalPosition >= this.lastSnap - 2;
  }

  readLength(value) {
    const length = Number.parseFloat(value);
    return Number.isFinite(length) ? length : 0;
  }

  syncScrollPadding() {
    const styles = window.getComputedStyle(this.track);
    const properties = [
      {
        key: 'start',
        style: 'scrollPaddingInlineStart',
        scrollPadding: 'scrollPaddingInlineStart',
        padding: 'paddingInlineStart',
      },
      {
        key: 'end',
        style: 'scrollPaddingInlineEnd',
        scrollPadding: 'scrollPaddingInlineEnd',
        padding: 'paddingInlineEnd',
      },
    ];

    properties.forEach(({ key, style, scrollPadding, padding }) => {
      if (this.managedScrollPadding[key] === null) {
        const inlineValue = this.track.style[style];
        const computedValue = this.readLength(styles[scrollPadding]);
        this.managedScrollPadding[key] = !inlineValue && computedValue <= 0;
      }

      if (this.managedScrollPadding[key]) {
        this.track.style[style] = `${this.readLength(styles[padding])}px`;
      }
    });
  }

  calculateSnapPositions() {
    const max = this.maxScroll;
    if (!this.items.length || max <= 1) return [0];

    const trackRect = this.track.getBoundingClientRect();
    const trackStyles = window.getComputedStyle(this.track);
    const current = this.logicalScroll;
    const scrollPaddingStart = this.readLength(trackStyles.scrollPaddingInlineStart);
    const scrollPaddingEnd = this.readLength(trackStyles.scrollPaddingInlineEnd);
    const snapportEnd = this.track.clientWidth - scrollPaddingEnd;
    const snapportCenter = scrollPaddingStart + (snapportEnd - scrollPaddingStart) / 2;
    const positions = this.items.flatMap((item) => {
      const itemStyles = window.getComputedStyle(item);
      const alignment = itemStyles.scrollSnapAlign.trim().split(/\s+/).pop();
      if (!alignment || alignment === 'none') return [];

      const itemRect = item.getBoundingClientRect();
      const inlineStart = this.isRTL ? trackRect.right - itemRect.right : itemRect.left - trackRect.left;
      const inlineEnd = this.isRTL ? trackRect.right - itemRect.left : itemRect.right - trackRect.left;
      const snapAreaStart = current + inlineStart - this.readLength(itemStyles.scrollMarginInlineStart);
      const snapAreaEnd = current + inlineEnd + this.readLength(itemStyles.scrollMarginInlineEnd);
      let position = snapAreaStart - scrollPaddingStart;

      if (alignment === 'center') position = (snapAreaStart + snapAreaEnd) / 2 - snapportCenter;
      if (alignment === 'end') position = snapAreaEnd - snapportEnd;

      return [Math.max(0, Math.min(max, position))];
    });

    if (!positions.length) return [0];

    return positions
      .sort((a, b) => a - b)
      .filter((position, index, sorted) => index === 0 || Math.abs(position - sorted[index - 1]) > 0.5);
  }

  scrollToPosition(position, behavior, force = false) {
    const left = this.isRTL ? -position : position;
    if (!force && Math.abs(this.track.scrollLeft - left) <= 0.5) return;
    const options = {
      left,
      behavior: this.reducedMotion ? 'auto' : 'smooth',
    };
    if (behavior) options.behavior = behavior;
    this.track.scrollTo(options);
  }

  refresh() {
    if (this.closest('header') && this.getClientRects().length === 0) return;
    const initializing = this.snapPositions.length === 0;
    const restoring = this.reconnectPosition !== null;
    const pending = this.pendingAlignment;
    const settling = initializing || restoring || pending !== null;
    const initialSnapType = pending?.snapType ?? this.track.style.scrollSnapType;
    if (settling) this.track.style.scrollSnapType = 'none';
    this.items = Array.from(this.querySelectorAll('[data-dd-carousel-item]'));
    const previousPositions = this.snapPositions.length ? this.snapPositions : this.calculateSnapPositions();
    const previousPosition = pending?.position ?? this.reconnectPosition ?? Math.max(
      previousPositions[0] ?? 0,
      Math.min(previousPositions[previousPositions.length - 1] ?? this.maxScroll, this.logicalScroll)
    );
    const wasAtStart = pending?.atStart ?? this.wasAtStart ?? previousPosition <= (previousPositions[0] ?? 0) + 2;
    const wasAtEnd = pending?.atEnd ?? this.wasAtEnd ?? previousPosition >= (previousPositions[previousPositions.length - 1] ?? 0) - 2;

    this.syncScrollPadding();
    this.snapPositions = this.calculateSnapPositions();

    const behavior = settling ? 'instant' : 'auto';
    const destination = wasAtStart ? this.firstSnap : wasAtEnd ? this.lastSnap : Math.max(this.firstSnap, Math.min(this.lastSnap, previousPosition));
    this.scrollToPosition(destination, behavior, settling);
    if (settling) {
      if (pending) pending.position = destination;
      else {
        this.pendingAlignment = { position: destination, atStart: wasAtStart, atEnd: wasAtEnd, snapType: initialSnapType, frame: null };
        this.pendingAlignment.frame = window.requestAnimationFrame(() => {
          this.pendingAlignment.frame = window.requestAnimationFrame(() => this.finishAlignment());
        });
      }
    }
    this.reconnectPosition = null;

    const overflowing = this.overflowing;
    if (this.controls) this.controls.hidden = !overflowing;
    this.track.toggleAttribute('data-dd-overflowing', overflowing);
    this.update();
    this.schedule();
  }

  finishAlignment(cancel = false) {
    const pending = this.pendingAlignment;
    if (!pending) return;
    window.cancelAnimationFrame(pending.frame);
    this.pendingAlignment = null;
    if (!cancel && this.isConnected) this.scrollToPosition(pending.position, 'instant', true);
    this.track.style.scrollSnapType = pending.snapType;
    if (!cancel && this.isConnected) { this.update(); this.schedule(); }
  }

  update() {
    const range = this.scrollRange;
    const position = range > 0 ? Math.max(0, Math.min(1, (this.logicalPosition - this.firstSnap) / range)) : 0;

    this.wasAtStart = this.atStart;
    this.wasAtEnd = this.atEnd;
    this.lastPosition = this.logicalPosition;

    const activeControl = document.activeElement;
    const previousDisabled = !this.loops && this.atStart;
    const nextDisabled = !this.loops && this.atEnd;

    if (this.previousButton) this.previousButton.disabled = previousDisabled;
    if (this.nextButton) this.nextButton.disabled = nextDisabled;

    if (activeControl === this.previousButton && previousDisabled && this.nextButton && !nextDisabled) {
      this.nextButton.focus({ preventScroll: true });
    } else if (activeControl === this.nextButton && nextDisabled && this.previousButton && !previousDisabled) {
      this.previousButton.focus({ preventScroll: true });
    }

    if (this.progress && this.indicator) {
      const railWidth = 160;
      const visibleRatio = this.track.clientWidth > 0 ? this.track.clientWidth / (this.track.clientWidth + range) : 1;
      const indicatorWidth = Math.max(48, Math.min(railWidth, railWidth * visibleRatio));
      const indicatorX = position * (railWidth - indicatorWidth);
      this.indicator.style.setProperty('--dd-carousel-indicator-width', `${indicatorWidth}px`);
      this.indicator.style.setProperty('--dd-carousel-indicator-x', `${indicatorX}px`);
      this.indicator.style.setProperty('--dd-carousel-indicator-direction', this.isRTL ? '-1' : '1');
      this.progress.setAttribute('role', 'progressbar');
      this.progress.setAttribute('aria-valuemin', '0');
      this.progress.setAttribute('aria-valuemax', '100');
      this.progress.setAttribute('aria-valuenow', String(Math.round(position * 100)));
      this.progress.setAttribute('aria-label', this.progress.dataset.label || 'Carousel');
    }
  }

  onScroll() {
    window.requestAnimationFrame(() => this.update());
    this.restartTimer();
  }

  move(direction) {
    this.finishAlignment(true);
    if (!this.overflowing) return;

    const current = this.logicalPosition;
    let destination;

    if (this.loops && direction < 0 && this.atStart) destination = this.lastSnap;
    else if (this.loops && direction > 0 && this.atEnd) destination = this.firstSnap;
    else if (direction < 0) destination = [...this.snapPositions].reverse().find((position) => position < current - 2);
    else destination = this.snapPositions.find((position) => position > current + 2);

    if (destination === undefined) destination = direction < 0 ? this.firstSnap : this.lastSnap;
    this.scrollToPosition(destination);
    this.restartTimer();
  }

  onControlClick(event, direction) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.move(direction);
  }

  pause(reason) {
    this.pauseReasons.add(reason);
    this.clearTimer();
  }

  resumeFrom(reason) {
    this.pauseReasons.delete(reason);
    this.schedule();
  }

  restartTimer() {
    this.clearTimer();
    this.schedule();
  }

  schedule() {
    this.clearTimer();
    if (!this.isConnected || !this.autoscroll || !this.overflowing || !this.visible || document.hidden || this.pauseReasons.size > 0 || this.pendingAlignment) return;
    if (!this.loops && this.atEnd) return;
    this.timer = window.setTimeout(() => this.move(1), this.dwell);
  }

  clearFocusoutTimer() {
    if (this.focusoutTimer !== null) window.clearTimeout(this.focusoutTimer);
    this.focusoutTimer = null;
  }

  clearTimer() {
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = null;
  }

  onVisibilityChange() {
    if (document.hidden) this.clearTimer();
    else this.schedule();
  }
}

if (!customElements.get('dd-carousel')) customElements.define('dd-carousel', DDCarousel);
